import { useCallback, useEffect, useRef, useState, type SetStateAction } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { api } from "../lib/api";

export interface NoteFiltersSetting {
  filter_options: string[];
}

/**
 * Drives the language / model-type prompt. New doctors start with
 * `show_on_login` true. The prompt lowers it once answered or dismissed.
 */
export interface LanguageModelPopupSetting {
  show_on_login?: boolean;
}

/** Set when a provider minimizes Smart Assist. It stays until they expand it. */
export interface SmartAssistSetting {
  minimized?: boolean;
}

export interface ProviderSettings {
  note_filters?: NoteFiltersSetting;
  smart_assist?: SmartAssistSetting;
  language_model_popup?: LanguageModelPopupSetting;
  [key: string]: unknown;
}

export interface ProviderSettingsResponse {
  settings: ProviderSettings;
  updated_at: string | null;
}

export type UpdateProviderSettingsPayload = { settings: ProviderSettings } | ProviderSettings;

export const providerSettingsKeys = {
  all: ["providerSettings"] as const,
  detail: (userId?: string) => ["providerSettings", userId || "current"] as const,
};

export function useProviderSettingsQuery(userId?: string, enabled = true) {
  return useQuery({
    queryKey: providerSettingsKeys.detail(userId),
    queryFn: async () => {
      const response = await api.get<ProviderSettingsResponse>("/provider-settings");
      return response;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateProviderSettingsMutation(userId?: string) {
  const queryClient = useQueryClient();
  const queryKey = providerSettingsKeys.detail(userId);

  return useMutation({
    mutationFn: async (payload: UpdateProviderSettingsPayload) => {
      const response = await api.patch<ProviderSettingsResponse>("/provider-settings", payload);
      return response;
    },
    onMutate: async (newSettingsPayload) => {
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData<ProviderSettingsResponse>(queryKey);

      if (previousData) {
        const patch: Record<string, unknown> =
          typeof newSettingsPayload === "object" &&
          newSettingsPayload !== null &&
          "settings" in newSettingsPayload &&
          typeof newSettingsPayload.settings === "object" &&
          newSettingsPayload.settings !== null
            ? (newSettingsPayload.settings as Record<string, unknown>)
            : (newSettingsPayload as Record<string, unknown>);

        queryClient.setQueryData<ProviderSettingsResponse>(queryKey, {
          ...previousData,
          settings: {
            ...previousData.settings,
            ...patch,
          },
        });
      }

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}

export interface SequencedMutationOptions {
  onSuccess?: (data: ProviderSettingsResponse) => void;
  onError?: (error: unknown) => void;
  onSettled?: (data: ProviderSettingsResponse | undefined, error: unknown) => void;
}

interface PendingMutation {
  payload: UpdateProviderSettingsPayload;
  resolvers: Array<(value: ProviderSettingsResponse) => void>;
  rejecters: Array<(reason?: unknown) => void>;
}

export function useSequencedProviderSettingsMutation(userId?: string) {
  const mutation = useUpdateProviderSettingsMutation(userId);
  const isPersistingRef = useRef(false);
  const pendingMutationRef = useRef<PendingMutation | null>(null);
  const mutationRef = useRef(mutation);
  const currentUserIdRef = useRef(userId);

  useEffect(() => {
    mutationRef.current = mutation;
  }, [mutation]);

  useEffect(() => {
    if (currentUserIdRef.current !== userId) {
      currentUserIdRef.current = userId;
      if (pendingMutationRef.current) {
        pendingMutationRef.current.rejecters.forEach((reject) =>
          reject(new Error("User changed before settings mutation was dispatched"))
        );
        pendingMutationRef.current = null;
      }
    }
  }, [userId]);

  const mutateSequenced = useCallback(
    (
      payload: UpdateProviderSettingsPayload,
      options?: SequencedMutationOptions
    ): Promise<ProviderSettingsResponse> => {
      return new Promise<ProviderSettingsResponse>((resolve, reject) => {
        const handleResolve = (value: ProviderSettingsResponse) => {
          try {
            options?.onSuccess?.(value);
            options?.onSettled?.(value, undefined);
          } finally {
            resolve(value);
          }
        };

        const handleReject = (reason: unknown) => {
          try {
            options?.onError?.(reason);
            options?.onSettled?.(undefined, reason);
          } finally {
            reject(reason);
          }
        };

        if (isPersistingRef.current) {
          if (pendingMutationRef.current) {
            pendingMutationRef.current.payload = payload;
            pendingMutationRef.current.resolvers.push(handleResolve);
            pendingMutationRef.current.rejecters.push(handleReject);
          } else {
            pendingMutationRef.current = {
              payload,
              resolvers: [handleResolve],
              rejecters: [handleReject],
            };
          }
          return;
        }

        isPersistingRef.current = true;

        (async () => {
          let currentPayload: UpdateProviderSettingsPayload | null = payload;
          let currentResolvers = [handleResolve];
          let currentRejecters = [handleReject];

          while (currentPayload !== null) {
            try {
              const res = await mutationRef.current.mutateAsync(currentPayload);
              currentResolvers.forEach((resFn) => resFn(res));
            } catch (err) {
              console.error("Failed to update provider settings:", err);
              currentRejecters.forEach((rejFn) => rejFn(err));
            }

            if (pendingMutationRef.current !== null) {
              currentPayload = pendingMutationRef.current.payload;
              currentResolvers = pendingMutationRef.current.resolvers;
              currentRejecters = pendingMutationRef.current.rejecters;
              pendingMutationRef.current = null;
            } else {
              currentPayload = null;
              currentResolvers = [];
              currentRejecters = [];
            }
          }

          isPersistingRef.current = false;
        })();
      });
    },
    []
  );

  return {
    ...mutation,
    mutate: (payload: UpdateProviderSettingsPayload, options?: SequencedMutationOptions) => {
      mutateSequenced(payload, options).catch(() => {});
    },
    mutateAsync: mutateSequenced,
    mutateSequenced,
  };
}

// Every Smart Assist host writes the same preference, so saves share one queue
// and land in click order.
let saveQueue: Promise<void> = Promise.resolve();

/**
 * Smart Assist panel expansion, shared by every host. Only provider actions
 * (`setPreferredExpanded`) are saved; `setIsExpanded` changes the panel for
 * layout reasons without touching the saved preference.
 */
export function useSmartAssistPanelState() {
  const settingsQuery = useProviderSettingsQuery();
  const { mutateAsync } = useUpdateProviderSettingsMutation();
  const [isExpanded, setIsExpandedState] = useState(true);
  const hasSyncedRef = useRef(false);
  const changedBeforeSyncRef = useRef(false);
  const ownPendingSavesRef = useRef(0);

  const isLoaded = settingsQuery.isSuccess;
  const isMinimized = settingsQuery.data?.settings?.smart_assist?.minimized === true;

  const setIsExpanded = useCallback((next: SetStateAction<boolean>) => {
    if (!hasSyncedRef.current) {
      changedBeforeSyncRef.current = true;
    }
    setIsExpandedState(next);
  }, []);

  useEffect(() => {
    if (!isLoaded) return;
    if (!hasSyncedRef.current) {
      hasSyncedRef.current = true;
      // Keep a change made while settings were loading.
      if (changedBeforeSyncRef.current) return;
    }
    // This host's queued saves update the cache one at a time; its latest click already set the panel.
    if (ownPendingSavesRef.current > 0) return;
    setIsExpandedState(!isMinimized);
  }, [isLoaded, isMinimized]);

  const setPreferredExpanded = useCallback(
    (expanded: boolean) => {
      setIsExpanded(expanded);
      ownPendingSavesRef.current += 1;
      saveQueue = saveQueue
        .then(() => mutateAsync({ smart_assist: { minimized: !expanded } }))
        .then(() => undefined)
        .catch(() => {
          toast.error("Couldn't save your Smart Assist preference. Please try again.");
        })
        .finally(() => {
          ownPendingSavesRef.current -= 1;
        });
    },
    [mutateAsync, setIsExpanded]
  );

  return { isExpanded, setIsExpanded, setPreferredExpanded };
}

export default useProviderSettingsQuery;
